import * as React from 'react'
import Link from 'next/link'
import { Slot } from '@radix-ui/react-slot'
import { cva, type VariantProps } from 'class-variance-authority'

import { type ButtonProps } from '@/components/ui/button'
import { cn } from '@/lib/utils/utils'

/** Tailwind v4 uses individual transform props (`scale`, `translate`) — must use `all`, not `transform`. */
const CUSTOM_BUTTON_MOTION_STYLE: React.CSSProperties = {
    transition: 'all 300ms ease-in-out'
}

const customButtonInteractiveBase = [
    'mwd-custom-button',
    'inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap text-sm font-medium',
    'ring-offset-background',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
    'disabled:pointer-events-none disabled:opacity-50',
    '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0'
].join(' ')

/** Full-width listing CTA: white surface, card shadow, transform-only hover (no shadcn Button). */
const lightListingSurfaceVariants = cva(
    [
        'mwd-custom-button inline-flex h-14 w-full cursor-pointer items-center justify-center gap-2 whitespace-nowrap',
        'rounded-3xl border-0 bg-white font-semibold text-slate-900',
        'shadow-[0_18px_48px_-28px_rgba(15,15,15,0.45),0_8px_24px_-18px_rgba(20,63,63,0.28)]',
        'scale-100 origin-center translate-y-0',
        'hover:border-0 hover:bg-white hover:text-slate-900 hover:-translate-y-1',
        '[&_svg]:pointer-events-none [&_svg]:size-4 [&_svg]:shrink-0 [&_svg]:text-slate-600',
        '[&_svg]:transition-colors [&_svg]:duration-[300ms] [&_svg]:ease-out',
        'hover:[&_svg]:text-slate-900',
        'motion-reduce:transition-none motion-reduce:hover:translate-y-0',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-dark-petrol/25 focus-visible:ring-offset-2',
        'disabled:cursor-not-allowed disabled:opacity-50'
    ].join(' ')
)

const customButtonVariants = cva(
    [
        'relative cursor-pointer overflow-hidden',
        'rounded-full font-semibold tracking-tight',
        'scale-100 origin-center translate-y-0',
        'hover:scale-[1.03] hover:-translate-y-0.5',
        'active:scale-[0.99] active:translate-y-0',
        'motion-reduce:transition-none motion-reduce:hover:scale-100 motion-reduce:hover:translate-y-0 motion-reduce:active:scale-100',
        'disabled:cursor-not-allowed',
        '[&_svg]:size-[1.05em]'
    ].join(' '),
    {
        variants: {
            variant: {
                primary: [
                    '!bg-primary !text-primary-foreground hover:!text-primary-foreground',
                    'shadow-[0_10px_28px_rgba(15,15,15,0.12)]',
                    'before:pointer-events-none before:absolute before:inset-0 before:rounded-full before:bg-gradient-to-b before:from-white/10 before:to-transparent',
                    'hover:!bg-[hsl(176_52%_21%)] hover:shadow-[0_14px_32px_rgba(15,15,15,0.16)]'
                ].join(' '),
                /** Destructive actions — same shape/motion as primary, red surface */
                danger: [
                    '!bg-red-600 !text-white hover:!bg-red-700 hover:!text-white',
                    'shadow-[inset_0_1px_0_0_rgba(255,255,255,0.12),0_10px_28px_-14px_rgb(220_38_38_/_0.42)]',
                    'before:pointer-events-none before:absolute before:inset-0 before:rounded-full before:bg-gradient-to-b before:from-white/10 before:to-transparent before:opacity-90',
                    'hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.14),0_14px_36px_-12px_rgb(185_28_28_/_0.45)]'
                ].join(' '),
                accent: [
                    '!bg-citrus !text-darkgrey shadow-[inset_0_1px_0_0_rgba(255,255,255,0.55),0_10px_28px_-12px_rgba(243,255,0,0.35)]',
                    'hover:!brightness-[1.04] hover:shadow-[inset_0_1px_0_0_rgba(255,255,255,0.65),0_14px_36px_-10px_rgba(243,255,0,0.45)]'
                ].join(' '),
                outline: [
                    'border border-slate-300/70!',
                    '!bg-background/80 !text-primary backdrop-blur-sm',
                    'shadow-[inset_0_1px_0_0_rgba(255,255,255,0.65),0_6px_20px_-14px_hsl(176_52%_19%_/_0.25)]',
                    'hover:border-slate-300/80! hover:!bg-offwhite/90 hover:shadow-[0_10px_28px_-14px_hsl(176_52%_19%_/_0.2)]'
                ].join(' '),
                ghost: ['!text-primary hover:!bg-primary/[0.07] hover:!text-primary'].join(' ')
            },
            size: {
                default: '!h-11 min-h-11 px-6 py-2.5 text-sm',
                sm: '!h-9 min-h-9 px-4 py-2 text-xs',
                lg: '!h-12 min-h-12 px-8 py-3 text-base',
                icon: '!size-11 p-0'
            }
        },
        defaultVariants: {
            variant: 'primary',
            size: 'default'
        }
    }
)

type CustomVariant = NonNullable<VariantProps<typeof customButtonVariants>['variant']> | 'lightListing'
type CustomSize = NonNullable<VariantProps<typeof customButtonVariants>['size']>
export interface CustomButtonProps extends Omit<ButtonProps, 'variant' | 'size'> {
    variant?: CustomVariant
    size?: CustomSize
    /** When `variant` is `lightListing`, renders a Next.js `Link` instead of a `<button>`. */
    href?: string
}

const CustomButton = React.forwardRef<HTMLButtonElement, CustomButtonProps>(
    ({ className, variant = 'primary', size = 'default', href, asChild = false, style, ...props }, ref) => {
        const motionStyle = { ...CUSTOM_BUTTON_MOTION_STYLE, ...style }

        if (variant === 'lightListing') {
            const { children, disabled, type = 'button', ...rest } = props
            const surfaceClass = cn(lightListingSurfaceVariants(), className)

            if (href) {
                return (
                    <Link
                        href={href}
                        className={cn(surfaceClass, disabled && 'pointer-events-none opacity-50')}
                        aria-disabled={disabled || undefined}>
                        {children}
                    </Link>
                )
            }

            return (
                <button
                    ref={ref}
                    type={type}
                    disabled={disabled}
                    className={surfaceClass}
                    style={motionStyle}
                    {...rest}>
                    {children}
                </button>
            )
        }

        const Comp = asChild ? Slot : 'button'

        return (
            <Comp
                ref={ref}
                className={cn(customButtonInteractiveBase, customButtonVariants({ variant, size }), className)}
                style={motionStyle}
                {...props}
            />
        )
    }
)
CustomButton.displayName = 'CustomButton'

export { CustomButton, customButtonVariants }
